import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Star,
  ShieldCheck,
  Clock,
  CheckCircle2,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ServiceIcon from "@/components/ServiceIcon";
import CTASection from "@/components/CTASection";
import { COMPANY, SERVICES, TRUST_INDICATORS, TESTIMONIALS, FAQS, SERVICE_AREAS } from "@/data/site";

const HERO_IMG = "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg";
const ABOUT_IMG = "https://images.pexels.com/photos/21314036/pexels-photo-21314036.jpeg";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[640px] lg:min-h-[760px] flex items-center bg-[#0F172A]">
        <img
          src={HERO_IMG}
          alt="Licensed plumber repairing pipes"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-3 py-1.5 mb-6 fade-in-up">
              <span className="h-2 w-2 bg-white rounded-full emergency-pulse" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase">
                24/7 Emergency Service
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.05] tracking-tight fade-in-up stagger-1">
              Reliable Plumbing &amp; Heating Services in Stoughton, MA
            </h1>
            <p className="mt-6 text-base sm:text-lg lg:text-xl text-slate-200 max-w-2xl leading-relaxed fade-in-up stagger-2">
              Licensed, insured, and serving homeowners and businesses for over
              10 years. From burst pipes to water heaters — we show up fast and
              get the job done right.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-in-up stagger-3">
              <a href={`tel:${COMPANY.phoneTel}`} data-testid="hero-call-now-button">
                <Button className="w-full sm:w-auto rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold text-base px-8 py-6">
                  <Phone className="h-5 w-5 mr-2" strokeWidth={1.75} />
                  Call {COMPANY.phone}
                </Button>
              </a>
              <Link to="/quote" data-testid="hero-quote-button">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto rounded-none bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0F172A] font-semibold text-base px-8 py-6"
                >
                  Request a Free Quote
                  <ArrowRight className="h-5 w-5 ml-2" strokeWidth={1.75} />
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-slate-300 fade-in-up stagger-4">
              <div className="flex items-center gap-2 text-sm">
                <ShieldCheck className="h-4 w-4 text-[#0284C7]" strokeWidth={1.75} />
                Licensed &amp; Insured
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 text-[#0284C7]" strokeWidth={1.75} fill="currentColor" />
                Highly rated locally
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-[#0284C7]" strokeWidth={1.75} />
                Fast response
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="bg-white border-b border-slate-200" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10">
          {TRUST_INDICATORS.map((t, i) => (
            <div
              key={i}
              data-testid={`trust-indicator-${i}`}
              className="flex items-start gap-3"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center bg-[#0284C7]/10 text-[#0284C7] flex-shrink-0">
                <ServiceIcon name={t.icon} className="h-5 w-5" />
              </span>
              <div>
                <div className="font-display font-bold text-sm lg:text-base text-[#0F172A] leading-tight">
                  {t.title}
                </div>
                <div className="text-xs lg:text-sm text-slate-500 mt-0.5">
                  {t.subtitle}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section id="services" className="bg-slate-50 py-20 lg:py-28" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
                What we do
              </span>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
                Full-service plumbing &amp; heating in Stoughton, MA
              </h2>
            </div>
            <p className="lg:max-w-md text-slate-600">
              From emergencies to remodels, our licensed plumbers handle it all
              — residential and commercial.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s, i) => (
              <div
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className="group bg-white p-8 hover:bg-[#0284C7] transition-colors duration-300"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#0284C7]/10 text-[#0284C7] group-hover:bg-white/15 group-hover:text-white transition-colors">
                  <ServiceIcon name={s.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#0F172A] group-hover:text-white">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 group-hover:text-white/85 leading-relaxed">
                  {s.description}
                </p>
                <ul className="mt-4 space-y-1.5">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700 group-hover:text-white/90">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#0284C7] group-hover:text-white flex-shrink-0" strokeWidth={1.75} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/services" data-testid="home-view-all-services-link">
              <Button className="rounded-none bg-[#0F172A] hover:bg-slate-800 text-white font-semibold px-8 py-6">
                View All Services
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ABOUT / WHY US ============ */}
      <section className="bg-white py-20 lg:py-28" data-testid="home-about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <img
              src={ABOUT_IMG}
              alt="Stoughton, MA — proudly serving our community"
              className="w-full h-[420px] lg:h-[520px] object-cover"
              loading="lazy"
            />
            <div className="hidden lg:block absolute -bottom-6 -right-6 bg-[#0284C7] text-white p-8 max-w-xs">
              <div className="font-display text-5xl font-black leading-none">10+</div>
              <div className="mt-2 text-sm tracking-[0.12em] uppercase font-semibold">
                Years serving Stoughton, MA
              </div>
            </div>
          </div>

          <div>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              About us
            </span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
              Your trusted local plumbers in Stoughton, MA
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Boston Express Plumbing and Heating LLC is a family-run, locally
              owned company built on quality workmanship, fast response times,
              and honest service. We've spent over a decade keeping homes and
              businesses in Stoughton, MA running smoothly.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Licensed & insured local plumbers",
                "Upfront pricing — no surprise fees",
                "Fast response, often same-day service",
                "Workmanship you can count on",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[#0F172A]">
                  <CheckCircle2 className="h-5 w-5 text-[#0284C7] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                  <span className="text-sm lg:text-base">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/about" data-testid="home-learn-more-link">
                <Button className="rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-8 py-5">
                  Learn About Us
                </Button>
              </Link>
              <a href={`tel:${COMPANY.phoneTel}`} data-testid="home-about-call-link">
                <Button variant="outline" className="rounded-none border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white font-semibold px-8 py-5">
                  <Phone className="h-4 w-4 mr-2" strokeWidth={1.75} />
                  {COMPANY.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              Real reviews
            </span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
              Trusted by homeowners &amp; businesses
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-card-${i}`}
                className="bg-white p-6 border border-slate-200 flex flex-col"
              >
                <div className="flex items-center gap-1 text-[#0284C7]">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4" strokeWidth={1.5} fill="currentColor" />
                  ))}
                </div>
                <p className="mt-4 text-sm text-slate-700 leading-relaxed flex-1">
                  "{t.text}"
                </p>
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <div className="font-semibold text-[#0F172A] text-sm">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICE AREA ============ */}
      <section className="bg-white py-20 lg:py-28" data-testid="service-area-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              Service area
            </span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
              Based in Stoughton. Serving a 20-mile radius.
            </h2>
            <p className="mt-4 text-slate-600">
              We're a locally based plumbing company at 3 Cabot Place, Suite 5,
              Stoughton, MA — and we service homes and businesses across the
              surrounding communities. Don't see your town? Give us a call.
            </p>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="service-area-call-link"
              className="mt-6 inline-flex items-center gap-2 text-[#0284C7] font-semibold"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              {COMPANY.phone}
            </a>
          </div>
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
              {SERVICE_AREAS.map((city, i) => (
                <div
                  key={i}
                  data-testid={`service-area-${i}`}
                  className="flex items-center gap-2 py-1.5 text-slate-700"
                >
                  <Wrench className="h-3.5 w-3.5 text-[#0284C7]" strokeWidth={1.75} />
                  {city}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="bg-slate-50 py-20 lg:py-28" data-testid="home-faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              FAQ
            </span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
              Common questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-slate-200"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="font-display text-left text-base lg:text-lg font-semibold text-[#0F172A] hover:no-underline py-5"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm lg:text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
